import React, { useCallback, useEffect, useState, useRef } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeTags } from 'Core/hooks';
// Components
import { NotificationManager } from 'Components/Notifications';

import {
  Grid,
  Container,
  Typography,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from "@mui/material";
import { MenuComponent } from 'Components/Menu';
import TableView, { Toolbar, ToobarButton } from 'Components/TableView';
import { Loading, Pill, Button, Checkbox, Tooltip } from 'Components';
import { TableButtons } from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security"
import useExport from "Components/Table/Export";
import moment from 'moment';

import CreateEditTag from './components/createEditTag';
import ViewEntitiesTag from './components/viewEntities';
import MergeTags from './components/mergeTags';
import DeleteTags from './components/deleteTags';

// Actions
import {
  searchAccountTags,
  searchAccountTagsParents,
  searchAccountTagsChildren,
  accountTagsOpenCreateEdit,
  accountTagsOpenViewEntitiesModal,
  accountTagsOpenCloseMergeTagsModal,
  accountTagsOpenCloseDeleteTagsModal,
  openImportTagsModal,
  clearRedux
} from "Redux/actions";

import './styles.scss';
import { ExportButton } from "../../../../components/TableView";
import { get, set } from "lodash";
import ModalImportTags from './components/modalImportTags';
import { useImportNotifications } from 'Core/hooks/useImportNotifications';
import { setImportNotificationTag } from './components/modalImportTags/redux/slice';
import ImportResultsModal from "../../../../core/views/importResultsModal";


export const validateTagPermissions = (user, entities, indexStarInOne = false) => {
  return entities.filter(item => {
    if (user.isSubUser) {
      if (item?.permissionSubUser && item?.permissionSubUser.length) {
        return HasPermission(user, item?.permission, item?.permissionSubUser)
      }
      return true;
    }
    return HasPermission(user, item.permission || "", item.permissionSubUser || [])
  })
    .map((item, index) => ({ ...item, index: indexStarInOne ? index + 1 : index }));
}

const TagsTab = (props) => {
  // Props
  const {
    intl: { messages }
  } = props;

  // Hooks
  const dispatch = useDispatch();

  //Stale local 
  const {
    tags,
    filters,
    totalTags,
    //Get only tag parent first level
    parents,
    loadingParents,
    parentsFilters,
    totalParents,
    //Get only children behind first level
    children,
    loadingChildren,
    error
  } = useSelector((state) => state.tagsRedux);
  const loadingTags = useSelector((state) => state.tagsRedux.loadingTags);

  const openCreateEdit = useSelector(state => state.createEditTagRedux.openCreateEdit);
  const openViewEntities = useSelector(state => state.viewEntitiesTagRedux.openViewEntities);
  const openMergeTags = useSelector(state => state.mergeTagsRedux.open);
  const openDeleteTags = useSelector(state => state.deleteTagsRedux.open);
  const user = useSelector((state) => state.securityRedux.user);
  const importState = useSelector((state) => state.tagsImport);
  const openImportTags = useSelector((state) => state.tagsImport.openImport);
  const allAccountTags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });

  // Permissions
  const editablePermissions = HasPermission(user, "", [334])

  const tagsTableRef = useRef();
  const tagsTreeTableRef = useRef();
  const [expanded, setExpanded] = React.useState([]);
  const [selected, setSelected] = React.useState([]);
  const [allTags, setAllTags] = React.useState([]);
  const [exportData, setExportData] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  useImportNotifications(
    state => state.tagsImport,
    setImportNotificationTag
  );

  useEffect(() => {
    return () => {
      dispatch(clearRedux("ACCOUNT_TAGS"));
    }
  }, []);
  //Monitor filters to set search mode, ensure only the searched data is shown in the export
  useEffect(() => {
    setIsSearchActive(!!filters.openSearch);
  }, [filters.openSearch]);

  useEffect(() => {
    if (!filters.searchText) {
      dispatch(searchAccountTagsParents({ ...filters }));
    }
  }, [filters])

  useEffect(() => {
    dispatch(searchAccountTagsParents({ ...parentsFilters, offset: parentsFilters.limit * 0 }));
  }, []);

  useEffect(() => {
    if (error) {
      NotificationManager.error(
        messages['errorMessage'],
        messages['error'],
        3000,
        null,
        null,
        'warning',
      );
      dispatch(clearRedux("ACCOUNT_TAGS"));
    }
  }, [error]);

  useEffect(() => {
    if (parents && children) {
      let newAllTags = parents?.filter(parent => !parent?.disabled);
      for (const [key, child] of Object.entries(children)) {
        newAllTags = [...newAllTags, ...child?.filter(c => !c?.disabled)];
      }

      setAllTags(newAllTags);
    }
  }, [parents, children]);

  const expand = useCallback((nodeId) => {
    let newExpanded = [...expanded];
    if (expanded.includes(nodeId)) {
      newExpanded = expanded.filter(e => e != nodeId);
    } else {
      newExpanded.push(nodeId);
    }

    setExpanded(newExpanded);
  });

  const cutString = useCallback((label, size = 20) => {
    if (label && label.length > size) {
      return `${label.slice(0, size)}...`;
    }

    return label;
  });

  const resetTable = useCallback(() => {
    if (filters.openSearch) {
      tagsTableRef?.current?.setNewPage(1);
      dispatch(searchAccountTags({ ...filters, offset: 0 }));
    } else {
      tagsTreeTableRef?.current?.setNewPage(1);
      dispatch(searchAccountTagsParents({ ...parentsFilters, offset: 0 }));
    }

    setExpanded([]);
    setSelected([]);

    dispatch(clearRedux("ACCOUNT_TAGS", { children: {} }));
  });

  useEffect(() => {
    
    if (importState?.importNotification?.status === "SUCCESS" || 
        importState?.importNotification?.status === "PARTIAL_SUCCESS") {
      resetTable();
    }
  }, [importState?.importNotification, resetTable]);

  const getHeaders = () => {
    return [
      messages["accountTags_id"],
      messages["accountTags_label"],
      messages["accountTags_parent"],   
      messages["accountTags_color"],
    ];
  };

  useEffect(() => {
    const prepareExportData = async () => {
      // Priority 1: If user has selected items, use only those
      if (selected?.length > 0) {
        const selectedForExport = selected.filter(tag => tag.id !== -1 && !tag.disabled);
        setExportData(selectedForExport);
        return;
      }

      // Priority 2: If in search mode, use current search results (parents)
      if (isSearchActive && parents?.length > 0) {
        const searchResults = (parents || []).filter(tag => tag.id !== -1 && !tag.disabled);
        setExportData(searchResults);
        return;
      }

      // Priority 3: Tree view - load children and combine with parents
      if (!loadingParents && parents?.length > 0) {
        // Find parents that have children but aren't loaded yet
        const parentsWithChildren = parents.filter(parent =>
          parent.hasChildren && !children[parent.id]
        );

        // Load missing children
        if (parentsWithChildren.length > 0) {
          for (const parent of parentsWithChildren) {
            await dispatch(searchAccountTagsChildren({ parentId: parent.id }));
          }
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Combine parents and all children
        let allTagsForExport = [...(parents || [])];
        Object.values(children || {}).forEach(childArray => {
          if (Array.isArray(childArray)) {
            allTagsForExport = [...allTagsForExport, ...childArray];
          }
        });

        const filteredData = allTagsForExport.filter(tag => tag.id !== -1 && !tag.disabled);
        setExportData(filteredData);
      }
    };

    prepareExportData();
  }, [selected, isSearchActive, parents, children, loadingParents, dispatch]);


  const getDataToExport = useCallback((data) => {
    const result = [];
    
    data.forEach((dataItem) => {
      const tagId = dataItem.id?.toString() || "";
      const tagLabel = dataItem.label || "";
      const tagColor = dataItem.color || "";
      const parentLabel = dataItem.parentId !== null 
        ? get(allAccountTags.find(t => t.id === dataItem.parentId), 'label', '')
        : '';
      
      result.push([tagId, tagLabel, parentLabel, tagColor]);
    });
    
    return result;
  }, [allAccountTags]);

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    exportData,
    null,
    `${messages['accountTags_tags']}-${moment().unix()}`,
    "landscape",
    true
  );

  const handleExport = useCallback((type) => {
    if (exportData.length === 0) {
      NotificationManager.warning('No data available for export');
      return;
    }

    if (type === 'Csv') {
      exportCSVPdf.exportCSV();
    } else if (type === 'Pdf') {
      exportCSVPdf.exportPDF();
    }
  }, [exportData, exportCSVPdf]);

  const renderHeader = useCallback((treeView = true) => {
    return (
      <Grid container className="tags-header">
        <Grid item>
          <div style={{ width: 40, height: 24 }}>
            <Checkbox
              indeterminate={selected.length > 0 && selected.length < allTags?.length}
              value={allTags?.length === selected?.length ? true : false}
              id="select-all-tags"
              style={{ padding: 0, marginLeft: 9 }}
              className="checkbox-no-padding"
              onChange={() => {
                if (treeView) {
                  if (allTags?.length === selected?.length) {
                    setSelected([]);
                  } else {
                    setSelected(allTags);
                  }
                }
              }}
            />
          </div>
        </Grid>

        <Grid item xs={treeView ? 6 : 3}>
          <span className="tags-header-label" style={{ paddingLeft: treeView ? 25 : 8 }}>{messages['accountTags_label']}</span>
        </Grid>
        {
          !treeView &&
          <Grid item xs={3}>
            <span className="tags-header-label">{messages['accountTags_path']}</span>
          </Grid>
        }
        <Grid item xs>
          <span className="tags-header-label">{messages['accountTags_units']}</span>
        </Grid>
        <Grid item xs>
          <span className="tags-header-label">{messages['accountTags_assets']}</span>
        </Grid>
        {
          HasPermission(user, "driver", [17]) && (
            <Grid item xs>
              <span className="tags-header-label">{messages['accountTags_drivers']}</span>
            </Grid>
          )
        }
        {
          HasPermission(user, "", [3]) && (
            <Grid item xs>
              <span className="tags-header-label">{messages['accountTags_landmarks']}</span>
            </Grid>
          )
        }
        {
          HasPermission(user, "", [1]) && (
            <Grid item xs>
              <span className="tags-header-label">{messages['accountTags_geofences']}</span>
            </Grid>
          )
        }
        {
          HasPermission(user, "", [31]) && (
            <Grid item xs>
              <span className="tags-header-label">{messages['accountTags_users']}</span>
            </Grid>
          )
        }

        {
          HasPermission(user, "dappfrm", [246]) && (
            <Grid item xs>
              <span className="tags-header-label">{messages['accountTags_dynamicForms']}</span>
            </Grid>
          )
        }
      </Grid>
    );
  });

  const tagItems = [
    { key: 'unitsCount', permission: '', permissionSubUser: [13] },
    { key: 'assetsCount', permission: '' },
    { key: 'driversCount', permission: 'driver', permissionSubUser: [17] },
    { key: 'landmarksCount', permission: '', permissionSubUser: [3] },
    { key: 'geofencesCount', permission: '', permissionSubUser: [1] },
    { key: 'usersCount', permission: '', permissionSubUser: [31] },
    { key: 'dynamicFormsCount', permission: 'dappfrm', permissionSubUser: [246] },

  ];

  const tagItemsWithPermissions = validateTagPermissions(user, tagItems)

  const renderTable = useCallback(() => {
    return (
      <TableView
        ref={tagsTableRef}
        data={tags || []}
        loading={loadingTags}
        rowsPerPage={filters.limit}
        name="tags-list-all"
        viewType="list"
        messages={messages}
        topSpaceSize={295}
        containerHeight="calc(100% - 60px)"
        itemSelector={false}
        renderHeader={() => renderHeader(false)}
        renderRow={(tag) => {
          let label = tag?.id == -1 ? messages[tag?.label] : tag?.label;
          return (
            <div>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                className="context-menu-container"
              >
                <Grid item>
                  <div
                    style={{
                      visibility: (tag?.disabled || tag?.id == -1) ? "hidden" : ""
                    }}
                  >
                    <Checkbox
                      value={selected.find(t => t.id === tag.id) ? true : false}
                      style={{ marginLeft: 0, marginRight: 0 }}
                      id={`${tag?.id}`}
                      onChange={() => {
                        let newSelected = [...selected];
                        if (selected.find(t => t.id === tag.id)) {
                          newSelected = selected.filter(t => t.id != tag.id);
                        } else {
                          newSelected.push(tag);
                        }

                        setSelected(newSelected);
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={3}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    className="context-menu-container"
                    wrap="nowrap"
                  >
                    <Grid item zeroMinWidth>
                      <Pill
                        textField="label"
                        item={{ ...tag, label: cutString(label) }}
                        outlined={false}
                        style={{ margin: 5 }}
                      />
                    </Grid>
                    <Grid item>
                      <MenuComponent
                        disabled={loadingTags || loadingParents || tag?.disabled || tag?.id == -1}
                        options={[
                          messages['edit'],
                          messages['delete']
                        ]}
                        icon={'more_vert'}
                        className="context-menu"
                        onClick={(type, value) => {
                          if (type === messages['edit']) {
                            dispatch(accountTagsOpenCreateEdit(true, value?.id));
                          } else if (type === messages['delete']) {
                            dispatch(accountTagsOpenCloseDeleteTagsModal(true, [value]));
                          }
                        }}
                        value={tag}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={3}>
                  <Tooltip title={tag?.path || ""}>
                    <span>
                      {cutString(tag?.path, 18)}
                    </span>
                  </Tooltip>
                </Grid>
                {tagItemsWithPermissions.map((item) => {
                  const count = tag[item.key] || 0;

                  return (
                    <Grid item xs key={item.key}>
                      <span
                        className={`${tag.disabled ? 'disabled' : ''} btn-link`}
                        style={{ marginLeft: 5, fontWeight: 'bold' }}
                        onClick={() => {
                          if (!tag.disabled) {
                            dispatch(accountTagsOpenViewEntitiesModal(true, tag, item.index));
                          }
                        }}
                      >
                        {count}
                      </span>
                    </Grid>
                  );
                })}
              </Grid>
            </div >
          )
        }}
        toolbarContainerClassName='table-toolbar-container'
        total={totalTags}
        serverSide={true}
        inputSearch={true}
        pagination={true}
        hideAllInPagination={true}
        onChangePage={(page) => {
          dispatch(searchAccountTags({ ...filters, offset: filters.limit * (page - 1), parentId: null }));
        }}
        onChangeRowsPerPage={(rowsPerPage) => {
          tagsTableRef?.current?.setNewPage(1);
          dispatch(searchAccountTags({ ...filters, offset: 0, limit: rowsPerPage }));
        }}
        onSearch={(searchText) => {
          setExportData([]);
          dispatch(clearRedux("ACCOUNT_TAGS", { children: {} }));
          tagsTableRef?.current?.setNewPage(1);
          dispatch(searchAccountTags({ ...filters, offset: 0, openSearch: searchText, parentId: null }));
        }}
      >
        <Toolbar toolbarClassName='table-toolbar'>
          {editablePermissions &&
            <>
              <div
                style={{ marginLeft: 4 }}
              >
                <ToobarButton
                  icon="add_box"
                  tooltip={messages['table_add']}
                  onClick={() => {
                    dispatch(accountTagsOpenCreateEdit(true));
                  }}
                />
              </div>

              <ToobarButton
                icon="delete"
                tooltip={messages['delete']}
                disabled={!selected?.length}
                onClick={() => {
                  dispatch(accountTagsOpenCloseDeleteTagsModal(true, selected));
                }}
              />

          <ToobarButton
            icon="merge_type"
            tooltip={messages['merge']}
            onClick={() => {
              dispatch(accountTagsOpenCloseMergeTagsModal(true, selected));
            }}
            disabled={false}
          />

          <TableButtons
            buttons={["export"]}
            disabled={false}
            onExport={handleExport}
          />
          <Button
              type="icon"
              icon="ios_share"
              tooltip={messages['accountTags_importTags']}
              onClick={() => {
                dispatch(openImportTagsModal(true));
              }}
              isDisabled={false}
          />
          </>
    }
        </Toolbar>
      </TableView >
    );
  });

  const renderTreeTable = useCallback((id, indent = 0) => {
    return (
      <TableView
        ref={!id ? tagsTreeTableRef : null}
        data={(id ? children[id] : parents) || []}
        loading={id ? loadingChildren[id] : loadingParents}
        rowsPerPage={id ? null : parentsFilters.limit}
        name={`tags-list-level-${id}`}
        viewType="list"
        messages={messages}
        topSpaceSize={id ? null : 295}
        height={id ? "100%" : null}
        containerHeight={id ? null : "calc(100% - 80px)"}
        itemSelector={false}
        renderHeader={id ? null : renderHeader}
        renderRow={(tag) => {
          let label = tag?.id == -1 ? messages[tag?.label] : tag?.label;
          return (
            <div
              style={{ minWidth: 650 }}
            >
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                className="context-menu-container"
              >
                <Grid item>
                  <div
                    style={{
                      visibility: (tag?.disabled || tag?.id == -1) ? "hidden" : ""
                    }}
                  >
                    <Checkbox
                      value={selected.find(t => t.id === tag.id) ? true : false}
                      id={`${tag?.id}`}
                      style={{ marginLeft: 0, marginRight: 0 }}
                      onChange={() => {
                        let newSelected = [...selected];
                        if (selected.find(t => t.id === tag.id)) {
                          newSelected = selected.filter(t => t.id != tag.id);
                        } else {
                          newSelected.push(tag);
                        }

                        setSelected(newSelected);
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={6}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ marginLeft: indent }}
                    wrap="nowrap"
                  >
                    <Grid item>
                      {
                        loadingChildren[tag.id] ?
                          <div style={{ marginRight: 5 }}><Loading size={20} /></div> :
                          <React.Fragment>
                            {
                              tag.hasChildren ?
                                <Button
                                  type="icon"
                                  icon={expanded.includes(tag.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                                  placement="bottom"
                                  className="btn-xs"
                                  fontSize="inherit"
                                  disabled={!tag.hasChildren}
                                  onClick={(e) => {
                                    expand(tag.id);
                                    if (!children[tag.id]) {
                                      dispatch(searchAccountTagsChildren({ parentId: tag.id }));
                                    }
                                  }}
                                /> :
                                <div style={{ width: 24, height: 24 }}></div>
                            }
                          </React.Fragment>
                      }
                    </Grid>
                    <Grid item xs>
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="flex-start"
                        className="context-menu-container"
                        wrap="nowrap"
                      >
                        <Grid item zeroMinWidth>
                          <Pill
                            textField="label"
                            item={{ ...tag, label: !id ? label : cutString(label) }}
                            outlined={false}
                          />
                        </Grid>
                        {editablePermissions &&
                          <Grid item>
                            <MenuComponent
                              disabled={loadingTags || loadingParents || tag?.disabled || tag?.id == -1}
                              options={[
                                messages['edit'],
                                messages['delete']
                              ]}
                              icon={'more_vert'}
                              className="context-menu"
                              onClick={(type, value) => {
                                if (type === messages['edit']) {
                                  dispatch(accountTagsOpenCreateEdit(true, value?.id));
                                } else if (type === messages['delete']) {
                                  dispatch(accountTagsOpenCloseDeleteTagsModal(true, [value]));
                                }
                              }}
                              value={tag}
                            />
                          </Grid>
                        }
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {tagItemsWithPermissions.map((item) => {
                  const count = tag[item.key] || 0;

                  return (
                    <Grid item xs key={item.key}>
                      <span
                        className={`${tag.disabled ? 'disabled' : ''} btn-link`}
                        style={{ marginLeft: 5, fontWeight: 'bold' }}
                        onClick={() => {
                          if (!tag.disabled) {
                            dispatch(accountTagsOpenViewEntitiesModal(true, tag, item.index));
                          }
                        }}
                      >
                        {count}
                      </span>
                    </Grid>
                  );
                })}
              </Grid>


              {
                (tag.hasChildren && children[tag.id] && children[tag.id].length > 0) &&
                <Collapse in={expanded.includes(tag.id)} timeout="auto" unmountOnExit>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: '0px 0px 0px 0px' }}
                  >
                    {renderTreeTable(tag.id, indent + 10)}
                  </Grid>
                </Collapse>
              }
            </div >
          )
        }}
        toolbarContainerClassName='table-toolbar-container'
        total={id ? children[id]?.length : totalParents}
        serverSide={!id ? true : false}
        inputSearch={!id ? true : false}
        pagination={!id ? true : false}
        hideAllInPagination={true}
        onChangePage={id ? null : (page) => {
          setSelected([])
          dispatch(searchAccountTagsParents({ ...parentsFilters, offset: parentsFilters.limit * (page - 1) }));
        }}
        onChangeRowsPerPage={id ? null : (rowsPerPage) => {
          tagsTreeTableRef?.current?.setNewPage(1);
          dispatch(searchAccountTagsParents({ ...parentsFilters, offset: 0, limit: rowsPerPage }));
        }}
        onSearch={id ? null : (searchText) => {
          setExportData([]);
          dispatch(clearRedux("ACCOUNT_TAGS", { children: {} }));
          tagsTableRef?.current?.setNewPage(1);
          dispatch(searchAccountTags({ ...filters, offset: 0, openSearch: searchText }));
        }}
      >
        {
          !id && editablePermissions &&
          <Toolbar toolbarClassName='table-toolbar'>
            <div
              style={{ marginLeft: 4 }}
            >
              <ToobarButton
                icon="add_box"
                tooltip={messages['table_add']}
                onClick={() => {
                  dispatch(accountTagsOpenCreateEdit(true));
                }}
              />
            </div>

            <ToobarButton
              icon="merge_type"
              tooltip={messages['merge']}
              onClick={() => {
                dispatch(accountTagsOpenCloseMergeTagsModal(true, selected?.filter(s => s?.id != -1)));
              }}
              disabled={false}
            />
            <TableButtons
              buttons={["export"]}
              disabled={false}
              onExport={handleExport}
            />
            <Button
              type="icon"
              icon="ios_share"
              tooltip={messages['accountTags_importTags']}
              onClick={() => {
                dispatch(openImportTagsModal(true));
              }}
              isDisabled={false}
            />

            {
              selected?.filter(s => s?.id != -1)?.length > 0 &&
              <ToobarButton
                icon="delete"
                tooltip={messages['delete']}
                disabled={!selected?.length}
                onClick={() => {
                  let availableTags = selected?.filter(s => s?.id != -1);
                  if (availableTags?.length) {
                    dispatch(accountTagsOpenCloseDeleteTagsModal(true, availableTags));
                  }
                }}
              />
            }

          </Toolbar>
        }
      </TableView >
    );
  });

  return (
    <Container className="account-tags-container" maxWidth={false}>
      <TableContainer>
        <Table
          stickyHeader={false}
          aria-label="Data View"
          size={'medium'}
          style={{ display: "table" }}
        >
          <TableBody>
            <TableRow>
              <TableCell style={{ minWidth: 1000, padding: 0, borderBottom: 'none' }}>
                {
                  filters.openSearch ?
                    renderTable() :
                    renderTreeTable(0)
                }
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <ImportResultsModal/>
      {
        openViewEntities && <ViewEntitiesTag />
      }

      {
        openMergeTags && <MergeTags callback={resetTable} />
      }
      {
        openImportTags && <ModalImportTags />
      }
      {
        openCreateEdit && <CreateEditTag callback={resetTable} />
      }

      {
        openDeleteTags && <DeleteTags callback={resetTable} />
      }

    </Container>
  );
}

export default injectIntl(TagsTab);